import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  Search,
  ThumbsUp,
  ThumbsDown,
  Instagram,
  Linkedin,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { traducoesArtigo, getArtigoDateLocale } from "@/i18n/artigo";
import { PublicHeader } from "@/components/PublicHeader";

interface Article {
  id: string;
  title: string;
  content: string;
  sourceUrl: string | null;
  updatedAt: string | null;
}

// Paleta: predominante #4b36cc, secundária #7f83fa, terciária #446eff
const BRAND_PRIMARY = "#4b36cc";
const BRAND_ACCENT = "#7f83fa";

// Artigo de exemplo quando a API está indisponível ou para /artigo/demo
const DEMO_ARTICLE: Article = {
  id: "demo",
  title: "Como acessar o produto que eu comprei?",
  content: `O acesso às compras é feito da seguinte forma:

1. Acesse o portal do consumidor em https://consumer.exemplo.com/ ou o aplicativo em https://app.exemplo.com/

2. Faça login com seu **Usuário ou E-mail** e **Senha**. Se precisar, use a opção EXIBIR SENHA para conferir.

3. No menu, clique em Produtos e depois em Minhas Compras.

4. Localize o produto desejado e clique para acessar o conteúdo.

Caso não tenha localizado a compra, verifique também o artigo "Como acessar o produto que comprei?" na Central de Ajuda.`,
  sourceUrl: null,
  updatedAt: new Date().toISOString(),
};

export default function Artigo() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const t = traducoesArtigo[lang];
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busca, setBusca] = useState("");
  const [feedback, setFeedback] = useState<"helped" | "not_helped" | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [recommended, setRecommended] = useState<{ id: string; title: string }[]>([]);

  // Fetch recommended articles (excluding current)
  useEffect(() => {
    if (!id || id.toLowerCase() === "demo") return;
    fetch(`/api/support/articles?limit=5&exclude=${id}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setRecommended(Array.isArray(data) ? data : []))
      .catch(() => setRecommended([]));
  }, [id]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    // /artigo/demo — mostra artigo de exemplo sem chamar a API
    if (id.toLowerCase() === "demo") {
      setArticle(DEMO_ARTICLE);
      setIsDemo(true);
      setLoading(false);
      return;
    }
    fetch(`/api/support/articles/${id}`)
      .then((res) => {
        if (res.status === 404) {
          setArticle(DEMO_ARTICLE);
          setIsDemo(true);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setArticle(data);
          setIsDemo(false);
        }
      })
      .catch(() => {
        setArticle(DEMO_ARTICLE);
        setIsDemo(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleBusca = () => {
    if (busca.trim()) navigate(`/search?query=${encodeURIComponent(busca)}`);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: BRAND_PRIMARY }} />
      </div>
    );

  if (notFound || (!article && !isDemo))
    return (
      <div
        className="min-h-screen flex flex-col bg-white text-[#3c3939] antialiased"
        style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
      >
          <PublicHeader />
        <section className="px-4 sm:px-6 py-6 border-b border-gray-200">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: BRAND_PRIMARY }} aria-hidden />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleBusca()}
                placeholder={t.buscarPlaceholder}
                className="w-full pl-12 pr-4 h-12 rounded-lg border border-gray-300 bg-white text-[#3c3939] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30 text-base"
                aria-label={t.buscarAriaLabel}
              />
            </div>
          </div>
        </section>
        <main className="flex-1 px-4 sm:px-6 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl font-bold text-[#3c3939] mb-2">{t.artigoNaoEncontrado}</p>
            <p className="text-sm text-gray-600 mb-4">
              {t.artigoNaoEncontradoDesc}
            </p>
            <Link to="/ajuda" className="inline-flex items-center text-sm font-medium hover:underline" style={{ color: BRAND_PRIMARY }}>
              {t.voltarCentral}
            </Link>
          </div>
        </main>
      </div>
    );

  return (
    <div
      className="min-h-screen flex flex-col bg-white text-[#3c3939] antialiased"
      style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
    >
      <PublicHeader />

      {/* Barra de busca — borda cinza clara, ícone laranja */}
      <section className="px-4 sm:px-6 py-6 border-b border-gray-200">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5"
              style={{ color: BRAND_PRIMARY }}
              aria-hidden
            />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleBusca()}
              placeholder={t.buscarPlaceholder}
              className="w-full pl-12 pr-4 h-12 rounded-lg border border-gray-300 bg-white text-[#3c3939] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-base"
              aria-label={t.buscarAriaLabel}
            />
          </div>
        </div>
      </section>

      {/* Conteúdo principal */}
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-3xl mx-auto">
          {/* Título no formato "Resultados para '...'" */}
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3c3939] mb-2">
            {t.resultadoPara} &quot;{article.title}&quot;
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {t.respostaIA}
          </p>
          {isDemo && (
            <p className="text-sm text-primary bg-primary/10 border border-primary/30 rounded-lg px-3 py-2 mb-6">
              {t.exemploArtigo}
            </p>
          )}

          {/* Corpo do artigo — links em laranja */}
          <div className="mb-10">
            {article.content.split("\n").map((line, i) => (
              <p key={i} className="text-base text-[#3c3939] leading-relaxed mb-3 whitespace-pre-wrap">
                {line}
              </p>
            ))}
          </div>

          {article.sourceUrl && (
            <p className="text-sm mb-6">
              <a
                href={article.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium hover:underline"
                style={{ color: BRAND_PRIMARY }}
              >
                {t.verFonteOriginal}
              </a>
            </p>
          )}
          {article.updatedAt && (
            <p className="text-xs text-gray-500 mb-8">
              {t.atualizadoEm} {new Date(article.updatedAt).toLocaleDateString(getArtigoDateLocale(lang))}
            </p>
          )}

          {/* Feedback — botões com contorno laranja, ícones thumbs up/down */}
          <section className="mb-10 pt-8 border-t border-gray-200">
            <p className="text-base font-bold text-[#3c3939] mb-1">{t.ajudouTitle}</p>
            <p className="text-sm text-gray-600 mb-4">
              {t.ajudouDesc}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setFeedback("helped")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                  feedback === "helped"
                    ? "text-white"
                    : "bg-white text-[#3c3939] hover:bg-primary/10"
                }`}
                style={{
                  borderColor: BRAND_PRIMARY,
                  ...(feedback === "helped" ? { backgroundColor: BRAND_PRIMARY } : {}),
                }}
              >
                <ThumbsUp className="h-4 w-4" style={{ color: feedback === "helped" ? "white" : BRAND_PRIMARY }} />
                {t.ajudouSim}
              </button>
              <button
                type="button"
                onClick={() => setFeedback("not_helped")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                  feedback === "not_helped"
                    ? "text-white"
                    : "bg-white text-[#3c3939] hover:bg-primary/10"
                }`}
                style={{
                  borderColor: BRAND_PRIMARY,
                  ...(feedback === "not_helped" ? { backgroundColor: BRAND_PRIMARY } : {}),
                }}
              >
                <ThumbsDown className="h-4 w-4" style={{ color: feedback === "not_helped" ? "white" : BRAND_PRIMARY }} />
                {t.ajudouNao}
              </button>
            </div>
          </section>

          {/* Artigos recomendados — links laranja, separados por linha cinza */}
          {(recommended.length > 0 || isDemo) && (
            <section>
              <h2 className="text-xl font-bold text-[#3c3939] mb-4">{t.artigosRecomendados}</h2>
              <ul className="divide-y divide-gray-200">
                {recommended.length > 0
                  ? recommended.map((art) => (
                      <li key={art.id}>
                        <Link
                          to={`/artigo/${art.id}`}
                          className="block py-3 text-base font-medium hover:underline no-underline"
                          style={{ color: BRAND_PRIMARY }}
                        >
                          {art.title}
                        </Link>
                      </li>
                    ))
                  : t.recommendedTitles.map((item, i) => (
                      <li key={i}>
                        <Link
                          to={item.href}
                          className="block py-3 text-base font-medium hover:underline no-underline"
                          style={{ color: BRAND_PRIMARY }}
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))
                }
              </ul>
            </section>
          )}
        </div>
      </main>

      {/* Footer — estilo claro da referência: linha cinza, logo + redes, idioma + links, copyright */}
      <footer className="mt-auto border-t border-gray-200 bg-white px-4 sm:px-6 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 no-underline text-[#3c3939]"
              >
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white text-sm font-bold"
                  style={{ backgroundColor: BRAND_PRIMARY }}
                >
                  RTG
                </span>
                <span className="text-lg font-semibold text-[#3c3939]">{t.centralAjuda}</span>
              </Link>
              <div className="flex items-center gap-2">
                <a href="#" className="text-gray-500 hover:text-gray-700" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-500 hover:text-gray-700" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="flex flex-col sm:items-end gap-2">
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <span>{t.idioma}</span>
                <div className="flex items-center gap-1">
                  {(["pt", "en", "es"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setLang(l)}
                      className={`px-2 py-1 rounded transition-colors ${lang === l ? "text-[#3c3939] font-semibold" : "hover:text-[#3c3939]"}`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-4 text-sm">
                <Link to="/" className="text-gray-600 hover:text-[#3c3939] no-underline">
                  {t.site}
                </Link>
                <Link to="/ajuda" className="text-gray-600 hover:text-[#3c3939] no-underline">
                  {t.plataforma}
                </Link>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-100">
            {t.copyright}
          </p>
        </div>
      </footer>
    </div>
  );
}
